import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye } from 'lucide-react';
import Modal from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import projectService from '../services/projectService';
import clientService from '../services/clientService';
import { ROLES } from '../config/permissions';

const ProjectListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: 'ACTIVE', client: '', fromDate: '', toDate: '', assignedTo: '' });
  const [developers, setDevelopers] = useState([]);
  const [stats, setStats] = useState({ totalValue: 0, totalPaid: 0, totalPending: 0, counts: { total: 0, inProgress: 0, finished: 0 } });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', client: '', startDate: '', estimatedEndDate: '' });

  const loadClients = useCallback(async () => {
    try {
      const data = await clientService.getClients({ limit: 200, isActive: true });
      setClients(data.data?.clients || data.clients || []);
    } catch (error) { console.error(error); }
  }, []);

  const loadDevelopers = useCallback(async () => {
    try {
      const data = await projectService.getAssignedUsers();
      setDevelopers(data.users || []);
    } catch (error) { console.error(error); }
  }, []);

  const loadProjects = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await projectService.getProjects({ 
        page, 
        limit: itemsPerPage, 
        search: searchTerm, 
        status: filters.status === 'ACTIVE' ? 'PLANNING,IN_PROGRESS,ON_HOLD' : filters.status, 
        client: filters.client,
        assignedTo: filters.assignedTo,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      });
      setProjects(data.projects || []);
      setStats(data.stats || { totalValue: 0, totalPaid: 0, totalPending: 0, counts: { total: 0, inProgress: 0, finished: 0 } });
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      showNotification('Error al cargar proyectos', 'error');
    } finally { setLoading(false); }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  useEffect(() => { loadClients(); loadDevelopers(); loadProjects(1); }, [loadProjects, loadClients, loadDevelopers]);
  useEffect(() => { loadProjects(1); }, [searchTerm, filters]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = await projectService.createProject(formData);
      showNotification(data.message, 'success');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', client: '', startDate: '', estimatedEndDate: '' });
      loadProjects();
    } catch (error) { showNotification(error.message, 'error'); }
  };

  const statusColors = {
    PLANNING: { bg: '#FEF3C7', text: '#D97706', label: 'Planificación' }, // Ámbar
    IN_PROGRESS: { bg: '#DBEAFE', text: '#2563EB', label: 'En Progreso' }, // Azul
    ON_HOLD: { bg: '#F3F4F6', text: '#6B7280', label: 'En Pausa' }, // Gris
    DELIVERED: { bg: '#D1FAE5', text: '#059669', label: 'Entregado' }, // Verde
    CANCELLED: { bg: '#FEE2E2', text: '#DC2626', label: 'Cancelado' } // Rojo
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">📊 Proyectos</h1>
          <p className="text-gray-600">Gestiona los proyectos de desarrollo activos</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus size={16} /> Nuevo Proyecto
        </button>
      </div>
      
      {/* Mini Dashboard */}
      {!loading && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ borderLeft: '4px solid #4F46E5' }}>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Total Liquidación</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#111827' }}>${stats.totalValue.toLocaleString()}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                <span style={{ fontSize: '0.7rem', color: '#6B7280' }}>Global proyectos filtrados</span>
              </div>
            </div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid #10B981' }}>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Total Pagado</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#059669' }}>${stats.totalPaid.toLocaleString()}</div>
              <div style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '4px' }}>
                {stats.totalValue > 0 ? `${Math.round(stats.totalPaid * 100 / stats.totalValue)}% del total` : '0% del total'}
              </div>
            </div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid #EF4444' }}>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Total Pendiente</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#DC2626' }}>${stats.totalPending.toLocaleString()}</div>
              <div style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '4px' }}>Abonos pendientes</div>
            </div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid #F59E0B' }}>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Proyectos</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#111827' }}>{stats.counts?.total || 0}</div>
              <div style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '4px', display: 'flex', gap: '8px' }}>
                <span>Activos: {stats.counts?.inProgress || 0}</span>
                <span>•</span>
                <span>Finalizados: {stats.counts?.finished || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card mb-6 filters-container" style={{ position: 'relative', zIndex: 100, overflow: 'visible' }}>
        <div className="card-content" style={{ overflow: 'visible' }}>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Nombre del proyecto..." className="form-input" />
            </div>
            <div style={{ width: '220px' }}>
              <CustomSelect label="Estado" value={filters.status} onChange={(v) => setFilters(p => ({ ...p, status: v }))}
                options={[
                  { value: 'ACTIVE', label: 'Proyectos Activos' },
                  { value: '', label: 'Todos los estados' }, 
                  { value: 'PLANNING', label: 'Planificación' },
                  { value: 'IN_PROGRESS', label: 'En Progreso' }, 
                  { value: 'ON_HOLD', label: 'En Pausa' },
                  { value: 'DELIVERED', label: 'Entregado' }, 
                  { value: 'CANCELLED', label: 'Cancelado' }
                ]} placeholder="Filtrar por estado"
              />
            </div>
            <div style={{ width: '180px' }}>
              <label className="form-label">Desde</label>
              <input type="date" value={filters.fromDate} onChange={(e) => setFilters(p => ({ ...p, fromDate: e.target.value }))} className="form-input" />
            </div>
            <div style={{ width: '180px' }}>
              <label className="form-label">Hasta</label>
              <input type="date" value={filters.toDate} onChange={(e) => setFilters(p => ({ ...p, toDate: e.target.value }))} className="form-input" />
            </div>
            <div className="w-64">
              <CustomSelect label="Cliente" value={filters.client} onChange={(v) => setFilters(p => ({ ...p, client: v }))}
                options={[{ value: '', label: 'Todos' }, ...clients.map(c => ({ value: c._id, label: c.name }))]} placeholder="Todos"
              />
            </div>
            <div className="w-64">
              <CustomSelect label="Responsable" value={filters.assignedTo} onChange={(v) => setFilters(p => ({ ...p, assignedTo: v }))}
                options={[{ value: '', label: 'Todos' }, ...developers.map(d => ({ value: d._id, label: d.personal?.firstName ? `${d.personal.firstName} ${d.personal.lastName || ''}`.trim() : d.email }))]} placeholder="Todos"
              />
            </div>
            <div style={{ width: '120px' }}>
              <label className="form-label">&nbsp;</label>
              <button onClick={() => { setSearchTerm(''); setFilters({ status: 'ACTIVE', client: '', fromDate: '', toDate: '', assignedTo: '' }); }} className="btn btn-outline w-full" style={{ padding: '8px' }}>🗑️ Limpiar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12"><div className="loading-spinner"></div><p className="mt-4 text-gray-600">Cargando proyectos...</p></div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay proyectos</h3>
            <p className="text-gray-600 mb-6">Crea tu primer proyecto o genera uno desde una propuesta IA</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Cliente</th>
                <th>Responsable</th>
                <th>Estado</th>
                <th style={{ minWidth: '100px' }}>Progreso</th>
                <th>Horas</th>
                <th style={{ textAlign: 'right' }}>Valor Dev.</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th>Estado Pago</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => {
                const settlement = project.developerSettlement || { totalAmount: 0, payments: [] };
                const paid = (settlement.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
                const total = settlement.totalAmount || 0;
                
                let paymentStatus = { label: 'Pendiente', color: '#6B7280', bg: '#F3F4F6' };
                if (total > 0) {
                  if (paid >= total) paymentStatus = { label: 'Pagado', color: '#059669', bg: '#D1FAE5' };
                  else if (paid > 0) paymentStatus = { label: 'Parcial', color: '#2563EB', bg: '#DBEAFE' };
                }

                return (
                  <tr key={project._id}>
                    <td>
                      <div className="font-medium text-gray-900">{project.name}</div>
                      {project.proposal && <div className="text-xs text-blue-600">🤖 Generado por IA</div>}
                    </td>
                    <td className="text-sm text-gray-700">{project.client?.name || 'N/A'}</td>
                    <td className="text-sm text-gray-700">
                      {project.assignedTo ? (
                        <div className="flex items-center gap-2">
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '700', color: '#4F46E5' }}>
                            {project.assignedTo.personal?.firstName?.[0] || project.assignedTo.email?.[0]?.toUpperCase()}
                          </div>
                          <span>{project.assignedTo.personal?.firstName ? `${project.assignedTo.personal.firstName} ${project.assignedTo.personal.lastName || ''}`.trim() : project.assignedTo.email}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-xs">Sin asignar</span>
                      )}
                    </td>
                    <td>
                      <span style={{ 
                        padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: statusColors[project.status]?.bg || '#F3F4F6', 
                        color: statusColors[project.status]?.text || '#6B7280' 
                      }}>
                        {statusColors[project.status]?.label || project.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ flex: 1, height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden', minWidth: '60px' }}>
                          <div style={{ 
                            width: `${project.overallProgress || 0}%`, height: '100%', 
                            background: project.overallProgress >= 100 ? 'linear-gradient(90deg, #10B981, #059669)' : 'linear-gradient(90deg, #3B82F6, #2563EB)', 
                            borderRadius: '3px', transition: 'width 0.3s' 
                          }} />
                        </div>
                        <span className="text-xs font-bold" style={{ color: project.overallProgress >= 100 ? '#059669' : '#111827' }}>
                          {project.overallProgress || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="text-xs text-gray-600 font-medium">
                      {project.totalActualHours || 0} / {project.totalEstimatedHours || 0}h
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>
                      ${total.toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.85rem', color: '#059669' }}>
                      ${paid.toLocaleString()}
                    </td>
                    <td>
                      <span style={{ 
                        padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700',
                        backgroundColor: paymentStatus.bg, color: paymentStatus.color,
                        border: `1px solid ${paymentStatus.color}33`
                      }}>
                        {paymentStatus.label}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/projects/${project._id}`)} className="btn btn-primary btn-sm flex items-center gap-2" title="Ver detalles">
                           <Eye size={16} /> Ver Detalle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info"><span>Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems}</span></div>
          <div className="pagination-controls">
            <button onClick={() => loadProjects(currentPage - 1)} disabled={currentPage === 1} className="btn btn-outline btn-sm">← Anterior</button>
            <button onClick={() => loadProjects(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-outline btn-sm">Siguiente →</button>
          </div>
        </div>
      )}

      {/* Modal Crear */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nuevo Proyecto" onSubmit={handleCreate} submitText="Crear">
        <div className="form-section">
          <h4 className="form-section-title">Información del Proyecto</h4>
          <CustomSelect label="Cliente" value={formData.client} onChange={(v) => setFormData(p => ({ ...p, client: v }))}
            options={[{ value: '', label: 'Seleccionar cliente' }, ...clients.map(c => ({ value: c._id, label: c.name }))]} placeholder="Seleccionar cliente"
          />
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Nombre del Proyecto <span className="label-required">*</span></label>
            <input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="form-input" placeholder="Ej. Plataforma E-Commerce" required />
          </div>
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Descripción</label>
            <textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="form-input form-textarea" rows="3" />
          </div>
        </div>
      </Modal>

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default ProjectListPage;
